/**
 * Dialling codes, and the split/join between one stored phone number and the two boxes that edit it.
 *
 * The API stores a single string per number (Customer.phone_number / mobile_number are
 * PhoneNumberFields), so the country code is not a separate column and must not become one. Everything
 * here is presentation: split the stored string into a code and a national number on the way into the
 * form, and join them back into one string on the way out. Nothing upstream of the badge changes shape.
 *
 * The list was 26 curated countries and is now every dialling code in use, because "not all countries
 * listed" is what operators reported: a customer outside the curated set had no row to pick, and the only
 * workaround was typing the code into the number box and hoping the split recognised it.
 *
 * It is hand-written rather than generated. react-select-country-list, already a dependency, carries names
 * and ISO codes but no dialling codes, and nothing else in the tree provides them — so a table has to exist
 * somewhere. This one is declared in dial-code order, which is how it is checked for gaps and duplicates,
 * and exported sorted by country, which is how it is read.
 *
 * ONE ROW PER CODE, not one row per country. Two rows reading "+44" would both light up as selected and
 * picking either would store the same string, so the countries that genuinely share a code share a row and
 * the label names them. Territories that share their parent's code (Christmas Island on +61, Svalbard on
 * +47, the Crown dependencies on +44) are left to the parent's row. The North American ones are the
 * exception: +1 territories have distinct four-digit codes, so Barbados and the rest get their own rows.
 */

// Grouped by ITU zone, ascending. Kept in this order so a missing or duplicated code is visible on sight.
const DIAL_CODES_BY_CODE = [
    // Zone 1 — North American Numbering Plan. +1 followed by the area code for the territories.
    {dial: '+1',    label: 'United States / Canada'},
    {dial: '+1242', label: 'Bahamas'},
    {dial: '+1246', label: 'Barbados'},
    {dial: '+1264', label: 'Anguilla'},
    {dial: '+1268', label: 'Antigua & Barbuda'},
    {dial: '+1284', label: 'British Virgin Islands'},
    {dial: '+1340', label: 'U.S. Virgin Islands'},
    {dial: '+1345', label: 'Cayman Islands'},
    {dial: '+1441', label: 'Bermuda'},
    {dial: '+1473', label: 'Grenada'},
    {dial: '+1649', label: 'Turks & Caicos Islands'},
    {dial: '+1664', label: 'Montserrat'},
    {dial: '+1670', label: 'Northern Mariana Islands'},
    {dial: '+1671', label: 'Guam'},
    {dial: '+1684', label: 'American Samoa'},
    {dial: '+1721', label: 'Sint Maarten'},
    {dial: '+1758', label: 'Saint Lucia'},
    {dial: '+1767', label: 'Dominica'},
    {dial: '+1784', label: 'Saint Vincent & the Grenadines'},
    {dial: '+1787', label: 'Puerto Rico'},
    {dial: '+1809', label: 'Dominican Republic'},
    {dial: '+1868', label: 'Trinidad & Tobago'},
    {dial: '+1869', label: 'Saint Kitts & Nevis'},
    {dial: '+1876', label: 'Jamaica'},
    // Zone 2 — Africa, plus a few Atlantic and Caribbean territories.
    {dial: '+20',   label: 'Egypt'},
    {dial: '+211',  label: 'South Sudan'},
    {dial: '+212',  label: 'Morocco'},
    {dial: '+213',  label: 'Algeria'},
    {dial: '+216',  label: 'Tunisia'},
    {dial: '+218',  label: 'Libya'},
    {dial: '+220',  label: 'Gambia'},
    {dial: '+221',  label: 'Senegal'},
    {dial: '+222',  label: 'Mauritania'},
    {dial: '+223',  label: 'Mali'},
    {dial: '+224',  label: 'Guinea'},
    {dial: '+225',  label: "Côte d'Ivoire"},
    {dial: '+226',  label: 'Burkina Faso'},
    {dial: '+227',  label: 'Niger'},
    {dial: '+228',  label: 'Togo'},
    {dial: '+229',  label: 'Benin'},
    {dial: '+230',  label: 'Mauritius'},
    {dial: '+231',  label: 'Liberia'},
    {dial: '+232',  label: 'Sierra Leone'},
    {dial: '+233',  label: 'Ghana'},
    {dial: '+234',  label: 'Nigeria'},
    {dial: '+235',  label: 'Chad'},
    {dial: '+236',  label: 'Central African Republic'},
    {dial: '+237',  label: 'Cameroon'},
    {dial: '+238',  label: 'Cape Verde'},
    {dial: '+239',  label: 'São Tomé & Príncipe'},
    {dial: '+240',  label: 'Equatorial Guinea'},
    {dial: '+241',  label: 'Gabon'},
    {dial: '+242',  label: 'Congo - Brazzaville'},
    {dial: '+243',  label: 'Congo - Kinshasa'},
    {dial: '+244',  label: 'Angola'},
    {dial: '+245',  label: 'Guinea-Bissau'},
    {dial: '+246',  label: 'British Indian Ocean Territory'},
    {dial: '+247',  label: 'Ascension Island'},
    {dial: '+248',  label: 'Seychelles'},
    {dial: '+249',  label: 'Sudan'},
    {dial: '+250',  label: 'Rwanda'},
    {dial: '+251',  label: 'Ethiopia'},
    {dial: '+252',  label: 'Somalia'},
    {dial: '+253',  label: 'Djibouti'},
    {dial: '+254',  label: 'Kenya'},
    {dial: '+255',  label: 'Tanzania'},
    {dial: '+256',  label: 'Uganda'},
    {dial: '+257',  label: 'Burundi'},
    {dial: '+258',  label: 'Mozambique'},
    {dial: '+260',  label: 'Zambia'},
    {dial: '+261',  label: 'Madagascar'},
    {dial: '+262',  label: 'Réunion / Mayotte'},
    {dial: '+263',  label: 'Zimbabwe'},
    {dial: '+264',  label: 'Namibia'},
    {dial: '+265',  label: 'Malawi'},
    {dial: '+266',  label: 'Lesotho'},
    {dial: '+267',  label: 'Botswana'},
    {dial: '+268',  label: 'Eswatini'},
    {dial: '+269',  label: 'Comoros'},
    {dial: '+27',   label: 'South Africa'},
    {dial: '+290',  label: 'Saint Helena'},
    {dial: '+291',  label: 'Eritrea'},
    {dial: '+297',  label: 'Aruba'},
    {dial: '+298',  label: 'Faroe Islands'},
    {dial: '+299',  label: 'Greenland'},
    // Zones 3 and 4 — Europe.
    {dial: '+30',   label: 'Greece'},
    {dial: '+31',   label: 'Netherlands'},
    {dial: '+32',   label: 'Belgium'},
    {dial: '+33',   label: 'France'},
    {dial: '+34',   label: 'Spain'},
    {dial: '+350',  label: 'Gibraltar'},
    {dial: '+351',  label: 'Portugal'},
    {dial: '+352',  label: 'Luxembourg'},
    {dial: '+353',  label: 'Ireland'},
    {dial: '+354',  label: 'Iceland'},
    {dial: '+355',  label: 'Albania'},
    {dial: '+356',  label: 'Malta'},
    {dial: '+357',  label: 'Cyprus'},
    {dial: '+358',  label: 'Finland'},
    {dial: '+359',  label: 'Bulgaria'},
    {dial: '+36',   label: 'Hungary'},
    {dial: '+370',  label: 'Lithuania'},
    {dial: '+371',  label: 'Latvia'},
    {dial: '+372',  label: 'Estonia'},
    {dial: '+373',  label: 'Moldova'},
    {dial: '+374',  label: 'Armenia'},
    {dial: '+375',  label: 'Belarus'},
    {dial: '+376',  label: 'Andorra'},
    {dial: '+377',  label: 'Monaco'},
    {dial: '+378',  label: 'San Marino'},
    {dial: '+380',  label: 'Ukraine'},
    {dial: '+381',  label: 'Serbia'},
    {dial: '+382',  label: 'Montenegro'},
    {dial: '+383',  label: 'Kosovo'},
    {dial: '+385',  label: 'Croatia'},
    {dial: '+386',  label: 'Slovenia'},
    {dial: '+387',  label: 'Bosnia & Herzegovina'},
    {dial: '+389',  label: 'North Macedonia'},
    {dial: '+39',   label: 'Italy / Vatican City'},
    {dial: '+40',   label: 'Romania'},
    {dial: '+41',   label: 'Switzerland'},
    {dial: '+420',  label: 'Czechia'},
    {dial: '+421',  label: 'Slovakia'},
    {dial: '+423',  label: 'Liechtenstein'},
    {dial: '+43',   label: 'Austria'},
    {dial: '+44',   label: 'United Kingdom'},
    {dial: '+45',   label: 'Denmark'},
    {dial: '+46',   label: 'Sweden'},
    {dial: '+47',   label: 'Norway'},
    {dial: '+48',   label: 'Poland'},
    {dial: '+49',   label: 'Germany'},
    // Zone 5 — Latin America and the remaining Caribbean.
    {dial: '+500',  label: 'Falkland Islands'},
    {dial: '+501',  label: 'Belize'},
    {dial: '+502',  label: 'Guatemala'},
    {dial: '+503',  label: 'El Salvador'},
    {dial: '+504',  label: 'Honduras'},
    {dial: '+505',  label: 'Nicaragua'},
    {dial: '+506',  label: 'Costa Rica'},
    {dial: '+507',  label: 'Panama'},
    {dial: '+508',  label: 'Saint Pierre & Miquelon'},
    {dial: '+509',  label: 'Haiti'},
    {dial: '+51',   label: 'Peru'},
    {dial: '+52',   label: 'Mexico'},
    {dial: '+53',   label: 'Cuba'},
    {dial: '+54',   label: 'Argentina'},
    {dial: '+55',   label: 'Brazil'},
    {dial: '+56',   label: 'Chile'},
    {dial: '+57',   label: 'Colombia'},
    {dial: '+58',   label: 'Venezuela'},
    {dial: '+590',  label: 'Guadeloupe / Saint Barthélemy / Saint Martin'},
    {dial: '+591',  label: 'Bolivia'},
    {dial: '+592',  label: 'Guyana'},
    {dial: '+593',  label: 'Ecuador'},
    {dial: '+594',  label: 'French Guiana'},
    {dial: '+595',  label: 'Paraguay'},
    {dial: '+596',  label: 'Martinique'},
    {dial: '+597',  label: 'Suriname'},
    {dial: '+598',  label: 'Uruguay'},
    {dial: '+599',  label: 'Curaçao / Caribbean Netherlands'},
    // Zone 6 — South East Asia and Oceania.
    {dial: '+60',   label: 'Malaysia'},
    {dial: '+61',   label: 'Australia'},
    {dial: '+62',   label: 'Indonesia'},
    {dial: '+63',   label: 'Philippines'},
    {dial: '+64',   label: 'New Zealand'},
    {dial: '+65',   label: 'Singapore'},
    {dial: '+66',   label: 'Thailand'},
    {dial: '+670',  label: 'Timor-Leste'},
    {dial: '+672',  label: 'Norfolk Island / Antarctica'},
    {dial: '+673',  label: 'Brunei'},
    {dial: '+674',  label: 'Nauru'},
    {dial: '+675',  label: 'Papua New Guinea'},
    {dial: '+676',  label: 'Tonga'},
    {dial: '+677',  label: 'Solomon Islands'},
    {dial: '+678',  label: 'Vanuatu'},
    {dial: '+679',  label: 'Fiji'},
    {dial: '+680',  label: 'Palau'},
    {dial: '+681',  label: 'Wallis & Futuna'},
    {dial: '+682',  label: 'Cook Islands'},
    {dial: '+683',  label: 'Niue'},
    {dial: '+685',  label: 'Samoa'},
    {dial: '+686',  label: 'Kiribati'},
    {dial: '+687',  label: 'New Caledonia'},
    {dial: '+688',  label: 'Tuvalu'},
    {dial: '+689',  label: 'French Polynesia'},
    {dial: '+690',  label: 'Tokelau'},
    {dial: '+691',  label: 'Micronesia'},
    {dial: '+692',  label: 'Marshall Islands'},
    // Zone 7 — shared, and not separable: Kazakh numbers are +7 6xx and +7 7xx.
    {dial: '+7',    label: 'Russia / Kazakhstan'},
    // Zone 8 — East Asia.
    {dial: '+81',   label: 'Japan'},
    {dial: '+82',   label: 'South Korea'},
    {dial: '+84',   label: 'Vietnam'},
    {dial: '+850',  label: 'North Korea'},
    {dial: '+852',  label: 'Hong Kong SAR'},
    {dial: '+853',  label: 'Macao SAR'},
    {dial: '+855',  label: 'Cambodia'},
    {dial: '+856',  label: 'Laos'},
    {dial: '+86',   label: 'China'},
    {dial: '+880',  label: 'Bangladesh'},
    {dial: '+886',  label: 'Taiwan'},
    // Zone 9 — South and West Asia.
    {dial: '+90',   label: 'Türkiye'},
    {dial: '+91',   label: 'India'},
    {dial: '+92',   label: 'Pakistan'},
    {dial: '+93',   label: 'Afghanistan'},
    {dial: '+94',   label: 'Sri Lanka'},
    {dial: '+95',   label: 'Myanmar'},
    {dial: '+960',  label: 'Maldives'},
    {dial: '+961',  label: 'Lebanon'},
    {dial: '+962',  label: 'Jordan'},
    {dial: '+963',  label: 'Syria'},
    {dial: '+964',  label: 'Iraq'},
    {dial: '+965',  label: 'Kuwait'},
    {dial: '+966',  label: 'Saudi Arabia'},
    {dial: '+967',  label: 'Yemen'},
    {dial: '+968',  label: 'Oman'},
    {dial: '+970',  label: 'Palestine'},
    {dial: '+971',  label: 'United Arab Emirates'},
    {dial: '+972',  label: 'Israel'},
    {dial: '+973',  label: 'Bahrain'},
    {dial: '+974',  label: 'Qatar'},
    {dial: '+975',  label: 'Bhutan'},
    {dial: '+976',  label: 'Mongolia'},
    {dial: '+977',  label: 'Nepal'},
    {dial: '+98',   label: 'Iran'},
    {dial: '+992',  label: 'Tajikistan'},
    {dial: '+993',  label: 'Turkmenistan'},
    {dial: '+994',  label: 'Azerbaijan'},
    {dial: '+995',  label: 'Georgia'},
    {dial: '+996',  label: 'Kyrgyzstan'},
    {dial: '+998',  label: 'Uzbekistan'},
];

/**
 * By country, because that is what an operator is looking for and there are now too many rows to scan for
 * a code. The dropdown shows the country first for the same reason: sorted by country but labelled
 * "+93 Afghanistan" reads as an unsorted list of codes, and MUI's type-ahead jumps on the leading text,
 * so a label starting with "+" makes typing "s" for Singapore do nothing.
 */
export const DIAL_CODES = [...DIAL_CODES_BY_CODE].sort((a, b) => a.label.localeCompare(b.label));

/** TapeArk is Australian, so a new contact starts on +61 rather than on a blank the operator must fill. */
export const DEFAULT_DIAL_CODE = '+61';

/*
 * Longest first, so +679 wins over a hypothetical +6 and the match is never ambiguous. This is what makes
 * the four-digit North American codes work: "+1246 555 1234" has to read as Barbados rather than as +1 with
 * "246 555 1234" left in the box. Safe in both directions because E.164 codes are prefix-free — no code is
 * the start of another — and the +1XXX rows are area codes inside +1, which no US or Canadian number uses.
 */
const DIALS_BY_LENGTH = DIAL_CODES.map(c => c.dial).sort((a, b) => b.length - a.length);

/**
 * Split a stored number into the code to select and the digits to show in the box.
 *
 * An unrecognised or code-less number (legacy data, a local number someone typed years ago) is returned
 * whole in `national` with the default code selected, and `matched` false. That is deliberate: the box
 * shows exactly what is stored rather than a guess at which country it belongs to. Opening such a customer
 * changes nothing, because the badge only ever joins a field the operator has actually edited; and if they
 * do edit it, the leading 0 those numbers carry fails validation with the correction in the message rather
 * than being quietly prefixed into "+61 0400 111 222", which is not a dialable number.
 */
export const splitPhoneNumber = (value) => {
    // trimStart, not trim, for the same reason joinPhoneNumber keeps a trailing space: this runs on every
    // keystroke and the result is what the box renders back.
    const trimmed = (value || '').trimStart();
    if (!trimmed.trim()) return {dial: DEFAULT_DIAL_CODE, national: '', matched: false};

    const withoutPlus = trimmed.replace(/^\+\s*/, '');
    const digitsOnly = withoutPlus.replace(/\D/g, '');
    const dial = DIALS_BY_LENGTH.find(d => digitsOnly.startsWith(d.slice(1)));
    if (!dial) return {dial: DEFAULT_DIAL_CODE, national: trimmed, matched: false};

    // Strip the code allowing for whatever spacing was used between its digits ("+61 4..." or "+6 1 4...").
    const spaced = dial.slice(1).split('').join('\\s*');
    const national = withoutPlus.replace(new RegExp(`^${spaced}\\s*`), '');
    return {dial, national, matched: true};
};

/**
 * Join a selected code and a typed national number back into the one string the API stores.
 * Blank number means blank field — a code on its own is not a phone number, and storing "+61" would
 * make an empty Mobile look filled to every required check downstream.
 */
export const joinPhoneNumber = (dial, national) => {
    // Leading whitespace goes, trailing whitespace stays. The box renders whatever the join produced, so
    // trimming the end would delete the space the operator just typed between two groups of digits — and
    // "400 333 444" could then only ever be typed as "400333444".
    const typed = (national || '').trimStart();
    if (!typed.trim()) return '';
    // Half-typed or unrecognised international number ("+6", "+998 12 345"): leave it as the operator has
    // it rather than gluing the selected code in front and inventing "+61 +6".
    if (typed.startsWith('+')) return typed;
    return `${dial} ${typed}`;
};

/**
 * What the two boxes should become after the operator types `typed` into the number box.
 *
 * Typing or pasting a full international number into the number box is the common accident — the number
 * arrives from an email as "+65 9123 4567" and goes in whole. Once enough of it has arrived to recognise
 * a code, that code moves into the select instead of being kept as digits. Until then the characters stay
 * in the box, so a "+" is not swallowed mid-keystroke.
 */
export const applyNationalInput = (typed, currentDial) => {
    const value = (typed || '').trimStart();
    if (!value.startsWith('+')) return {dial: currentDial, national: value};
    const parsed = splitPhoneNumber(value);
    return parsed.matched ? {dial: parsed.dial, national: parsed.national} : {dial: currentDial, national: value};
};
